import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { TaskNavigationSectionSchema } from "@nexus/contracts";
import { assertPagePermission } from "@/features/auth/lib/assert-page-permission";
import {
  adPlatformsQueryOptions,
  pageStateQueryOptions,
  PageAccessError,
  personaOpportunitySetsQueryOptions,
  personaOpportunitySetDetailQueryOptions,
  personaOpportunitySetKeys,
  personaOpportunityHubKeys,
  normalizedPersonaOpportunitySetTenantId,
  useGeneratePersonaOpportunitySet,
  useDeletePersonaOpportunitySet,
  useRenamePersonaOpportunitySet,
  useRetryPersonaOpportunitySet,
  useRegeneratePersonaOpportunitySet,
  activeTasksQueryOptions,
  PERSONA_SCRIPTS_LIVE_REFETCH_MS,
} from "@/lib/queries";
import { PageError, PageAccessErrorComponent } from "@/features/auth/components/PageError";
import { usePageState } from "@/hooks/usePageState";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { PersonaOpportunitiesPage } from "@/features/persona-opportunities/components/page/PersonaOpportunitiesPage/PersonaOpportunitiesPage";
import {
  legacyFacetsFromSearch,
  legacyFacetsToSearch,
} from "@/features/persona-opportunities/components/page/PersonaOpportunityHubChrome/hub-chrome-filters";
import { PersonaOpportunitiesLoadingState } from "@/features/persona-opportunities/components/page/PersonaOpportunitiesLoadingState/PersonaOpportunitiesLoadingState";
import { usePersonaOpportunityPermissions } from "@/features/persona-opportunities/hooks/usePersonaOpportunityPermissions";
import { useInvalidatePersonaDataOnScriptsGenerationTaskEnd } from "@/features/persona-opportunities/hooks/useInvalidatePersonaDataOnScriptsGenerationTaskEnd";
import { useInvalidatePersonaSetsOnGenerationTaskEnd } from "@/features/persona-opportunities/hooks/useInvalidatePersonaSetsOnGenerationTaskEnd";
import { useRefetchPersonaScriptsOnBroadcastEnd } from "@/features/persona-opportunities/hooks/useRefetchPersonaScriptsOnBroadcastEnd";
import { usePersonaSetGenerationToasts } from "@/features/persona-opportunities/hooks/usePersonaSetGenerationToasts";
import { buildGeneratePersonaOpportunitySetRequest } from "@/features/persona-opportunities/api/persona-opportunity-sets.api";
import type {
  PersonaOpportunitySetDetail,
  PersonaOpportunitySetListPage,
} from "@/features/persona-opportunities/api/persona-opportunity-sets.api";
import { toPersonaOpportunitySet } from "@/features/persona-opportunities/lib/persona-opportunity-set-mapper";
import {
  isPersonaScriptsGenerationActiveForAnySet,
  isPersonaScriptsGenerationActiveForSet,
} from "@/features/persona-opportunities/lib/persona-scripts-generation-active";
import {
  PERSONA_SCRIPTS_DOCUMENT_KIND_VALUES,
  PERSONA_SCRIPTS_LIST_SEARCH_KEYS,
  PERSONA_SCRIPTS_PLATFORM_VALUES,
  PERSONA_SCRIPTS_SENT_STATUS_VALUES,
  PERSONA_SCRIPTS_YES_FILTER_VALUES,
} from "@/features/persona-opportunities/lib/persona-scripts-list-search";
import {
  applyOptimisticCreateToList,
  createOptimisticGeneratingSetRow,
  createOptimisticPersonaSetRow,
  createPendingPersonaSetId,
  isPendingPersonaSetId,
  markPersonaSetGeneratingInDetail,
  markPersonaSetGeneratingInList,
  removePersonaSetFromList,
  replacePersonaSetIdInList,
} from "@/features/persona-opportunities/lib/persona-opportunity-set-generation-state";
import { clearPersonaOpportunitySetSuppressions } from "@/features/persona-opportunities/lib/persona-opportunity-set-suppression";
import { cancelTask, dismissTask } from "@/features/task-progress/api/task-progress.api";
import { useStopPersonaOpportunityGeneration } from "@/features/task-progress/hooks/useStopPersonaOpportunityGeneration";
import { useTaskSectionJump } from "@/features/task-progress/hooks/useTaskSectionJump";
import type { OpportunityHubTabKey } from "@/features/persona-opportunities/components/page/PersonaOpportunityHubChrome/hub-chrome.types";
import type {
  CreatePersonaSetSubmit,
  PersonaOpportunityGroupBy,
  PersonaOpportunitySet,
  PersonaOpportunityStatusFilter,
  PersonaOpportunityTabKey,
  PersonaOpportunityViewMode,
} from "@/features/persona-opportunities/types/persona-opportunities.types";

const TAB_VALUES: ["opportunities", "scripts", "sent_to_vendor"] = [
  "opportunities",
  "scripts",
  "sent_to_vendor",
];

const VIEW_VALUES: ["cards", "table"] = ["cards", "table"];

const GROUP_BY_VALUES: ["persona", "angle", "set"] = ["persona", "angle", "set"];

const STATUS_FILTER_VALUES: [
  "all",
  "NEW",
  "SCRIPTS_GENERATED",
  "BRIEFS_GENERATED",
  "SCRIPTS_AND_BRIEFS_GENERATED",
  "SENT_TO_VENDOR",
] = [
  "all",
  "NEW",
  "SCRIPTS_GENERATED",
  "BRIEFS_GENERATED",
  "SCRIPTS_AND_BRIEFS_GENERATED",
  "SENT_TO_VENDOR",
];

/** Query booleans arrive as strings; `z.coerce.boolean()` treats `"false"` as truthy. */
const booleanSearchParam = z
  .union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")])
  .optional();

/** Digit-only search is decoded as a number; keep it as the typed query string. */
const stringSearchParam = z.preprocess(
  (value) => (typeof value === "number" && Number.isFinite(value) ? String(value) : value),
  z.string().optional(),
).catch(undefined);

const HUB_TAB_VALUES: ["sets", "opportunities", "scripts", "sent_to_vendor"] = [
  "sets",
  "opportunities",
  "scripts",
  "sent_to_vendor",
];

/** Workspace `tab` values that have a hub counterpart. Anything else opens Opportunities. */
function hubTabForWorkspaceTab(
  tab: string | undefined,
): "opportunities" | "scripts" | "sent_to_vendor" {
  if (tab === "scripts" || tab === "sent_to_vendor") return tab;
  return "opportunities";
}

const personaOpportunitiesSearchSchema = z.object({
  setId: z.string().optional().catch(undefined),
  /** Opportunity Hub tab — only read when no `setId` opens the workspace. */
  hubTab: z.enum(HUB_TAB_VALUES).catch("sets"),
  /** Comma-separated hub set scope. Empty means every set of the tenant. */
  setIds: z.string().optional().catch(undefined),
  orderId: z.string().optional().catch(undefined),
  tab: z.enum(TAB_VALUES).catch("opportunities"),
  view: z.enum(VIEW_VALUES).catch("table"),
  groupBy: z.enum(GROUP_BY_VALUES).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  status: z.enum(STATUS_FILTER_VALUES).catch("all"),
  /** Legacy no-persona facets. `dataset` and `flowName` stay the same value. */
  action: z.string().optional().catch(undefined),
  dataset: z.string().optional().catch(undefined),
  flowName: z.string().optional().catch(undefined),
  feature: z.string().optional().catch(undefined),
  subFeature: z.string().optional().catch(undefined),
  engine: z.string().optional().catch(undefined),
  /**
   * Scripts-tab list lenses. Dedicated names so they never collide with angles
   * `q` / `status` or the storyboard deep-link `variant`. Keys come from
   * PERSONA_SCRIPTS_LIST_SEARCH_KEYS so read/write/validation stay aligned.
   */
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.q]: stringSearchParam,
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.kind]: z
    .enum(PERSONA_SCRIPTS_DOCUMENT_KIND_VALUES)
    .optional()
    .catch(undefined),
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sent]: z
    .enum(PERSONA_SCRIPTS_SENT_STATUS_VALUES)
    .optional()
    .catch(undefined),
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.sketch]: z
    .enum(PERSONA_SCRIPTS_YES_FILTER_VALUES)
    .optional()
    .catch(undefined),
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.animatic]: z
    .enum(PERSONA_SCRIPTS_YES_FILTER_VALUES)
    .optional()
    .catch(undefined),
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.favorites]: booleanSearchParam.catch(undefined),
  [PERSONA_SCRIPTS_LIST_SEARCH_KEYS.platform]: z
    .enum(PERSONA_SCRIPTS_PLATFORM_VALUES)
    .optional()
    .catch(undefined),
  createSet: booleanSearchParam.catch(undefined),
  /**
   * A generation started on another page (the persona library's create-set flow),
   * handed over as its optimistic row id plus correlation id so this route can
   * adopt the run. Consumed and cleared on arrival.
   */
  pendingSetId: z.string().optional().catch(undefined),
  pendingCorrelationId: z.string().optional().catch(undefined),
  /** Script id whose animatic storyboard modal is open — shareable link state. */
  storyboard: z.string().optional().catch(undefined),
  /** Deep-link: auto-expand this script's group and row. */
  scriptId: z.string().optional().catch(undefined),
  /** Deep-link: auto-expand this brief's group and card. */
  briefId: z.string().optional().catch(undefined),
  /** Angle group to expand when a generate-scripts card has no scriptId. */
  opportunityId: z.string().optional().catch(undefined),
  /**
   * One-shot deep-link seed from a completed storyboard task card — selects the
   * Sketch/Animatic toggle. Cleared after the storyboard view applies it; not
   * persistent UI state.
   */
  variant: z.enum(["sketch", "panels"]).optional().catch(undefined),
  /** Precise scroll target from `TaskNavigation.itemIds` — see `TASK_ITEM_SEARCH_PARAM`. */
  highlight: z.string().optional().catch(undefined),
  /** Region a completed task card asked to jump to — see `TASK_NAVIGATION_SECTION`. */
  section: TaskNavigationSectionSchema.optional().catch(undefined),
});

/** Exported for unit tests of deep-link search params. */
export { personaOpportunitiesSearchSchema };

type PersonaOpportunitiesSearch = z.infer<typeof personaOpportunitiesSearchSchema>;

const defaultSearch: PersonaOpportunitiesSearch = {
  tab: "opportunities",
  hubTab: "sets",
  view: "table",
  status: "all",
};

/** Stable empty scope so a tenant switch does not allocate a new array each render. */
const EMPTY_HUB_SET_IDS: string[] = [];

interface PendingGeneration {
  correlationId: string;
  /** Optimistic list row standing in until the worker publishes the real set id. */
  placeholderId: string;
  /** Open the workspace once that real id arrives. */
  autoOpen: boolean;
  /**
   * The run's task record has been seen in at least one poll. Until then its
   * absence means "not discovered yet", not "finished".
   */
  taskSeen: boolean;
}

function PersonaOpportunitiesRoute() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const userEmail = useAuth((s) => s.user?.email);
  const { isLoading, error } = usePageState("persona-opportunities");
  const { canListSets, canCreateSet, canCreateEmptyCustomSet, canUseQueryTuning } =
    usePersonaOpportunityPermissions();
  const stopPersonaOpportunityGeneration = useStopPersonaOpportunityGeneration();
  useTaskSectionJump();

  useDocumentTitle(t("page.title.personaOpportunities", "Opportunity Hub"));

  // One entry per generation whose optimistic card is still waiting for the
  // worker's real set id. A tenant may run several at once, so the placeholder
  // and the "open it when it lands" intent belong to a correlation id rather
  // than to the page — two concurrent runs would otherwise overwrite each
  // other's bookkeeping and strand the loser's card forever.
  //
  // Retry and regenerate-overwrite reuse an existing set id, so they have
  // nothing to reconcile and are never tracked here.
  const [pendingGenerations, setPendingGenerations] = React.useState<
    readonly PendingGeneration[]
  >([]);

  const {
    data: listPage,
    isLoading: setsLoading,
    isError: setsError,
  } = useQuery(
    personaOpportunitySetsQueryOptions(activeTenantId, {
      enabled: canListSets,
    }),
  );

  const sets = React.useMemo<PersonaOpportunitySet[]>(
    () => (listPage?.items ?? []).map(toPersonaOpportunitySet),
    [listPage],
  );

  const { data: activeTasks = [] } = useQuery({
    ...activeTasksQueryOptions(userEmail ?? undefined),
    enabled: Boolean(userEmail),
  });

  useInvalidatePersonaSetsOnGenerationTaskEnd(activeTasks, activeTenantId);
  useRefetchPersonaScriptsOnBroadcastEnd(activeTasks);
  useInvalidatePersonaDataOnScriptsGenerationTaskEnd(activeTasks, activeTenantId);

  // `setId` used to open the single-set workspace. That surface is retired, so the
  // page never selects it — a redirect below rewrites the URL onto the hub.
  const selectedSet: PersonaOpportunitySet | null = null;

  const navigateRef = React.useRef(navigate);
  navigateRef.current = navigate;

  /** Lands on the hub scoped to one set — the way into a set since the workspace retired. */
  const openHubForSet = React.useCallback((
    setId: string,
    options?: { replace?: boolean; hubTab?: OpportunityHubTabKey },
  ) => {
    void navigateRef.current({
      search: (previous) => ({
        ...previous,
        setId: undefined,
        setIds: setId,
        hubTab: options?.hubTab ?? "opportunities",
        createSet: undefined,
      }),
      replace: options?.replace,
    });
  }, []);

  const openScopedSet = React.useCallback(
    (setId: string, hubTab: OpportunityHubTabKey) => openHubForSet(setId, { hubTab }),
    [openHubForSet],
  );

  // Bookmarks and task cards still carry `setId`. Send them to the hub tab that
  // matches the old workspace tab, and drop `setId` so the workspace cannot open.
  React.useEffect(() => {
    if (!search.setId) return;
    const setId = search.setId;
    const hubTab = hubTabForWorkspaceTab(search.tab);
    void navigateRef.current({
      search: (previous) => ({
        ...previous,
        setId: undefined,
        setIds: setId,
        hubTab,
      }),
      replace: true,
    });
  }, [search.setId, search.tab]);

  // Switching tenants invalidates the open set and the hub's set selection.
  // Caches are tenant-keyed; the URL is the piece that would otherwise leak.
  const previousTenantIdRef = React.useRef(activeTenantId);
  React.useEffect(() => {
    if (previousTenantIdRef.current === activeTenantId) return;
    previousTenantIdRef.current = activeTenantId;
    setPendingGenerations([]);
    clearPersonaOpportunitySetSuppressions();

    if (search.setId || search.setIds) {
      void navigateRef.current({
        search: (previous) => ({ ...previous, setId: undefined, setIds: undefined }),
        replace: true,
      });
    }
  }, [activeTenantId, search.setId, search.setIds]);

  // A suppression only makes sense for the page instance that created it.
  React.useEffect(() => clearPersonaOpportunitySetSuppressions, []);

  /**
   * Registered from the mutation's own `onSuccess`, where the placeholder that
   * this correlation id belongs to is still in scope.
   */
  const trackPendingGeneration = React.useCallback(
    (correlationId: string, placeholderId: string, autoOpen: boolean) => {
      setPendingGenerations((prev) => [
        ...prev,
        { correlationId, placeholderId, autoOpen, taskSeen: false },
      ]);
    },
    [],
  );

  // A run started from the persona library arrives already in flight: its optimistic
  // row is in the list cache and its correlation id is in the URL. Adopting it here
  // is what lets the workspace open when the worker publishes the real set id, and
  // what clears the placeholder if the run ends without ever publishing one.
  const { pendingSetId, pendingCorrelationId } = search;
  const didAdoptHandedOverGenerationRef = React.useRef(false);
  React.useEffect(() => {
    if (didAdoptHandedOverGenerationRef.current) return;
    if (!pendingSetId || !pendingCorrelationId) return;
    didAdoptHandedOverGenerationRef.current = true;
    trackPendingGeneration(pendingCorrelationId, pendingSetId, true);
    void navigateRef.current({
      search: (previous) => ({
        ...previous,
        pendingSetId: undefined,
        pendingCorrelationId: undefined,
      }),
      replace: true,
    });
  }, [pendingSetId, pendingCorrelationId, trackPendingGeneration]);

  const untrackPendingGeneration = React.useCallback((correlationId: string) => {
    setPendingGenerations((prev) =>
      prev.filter((generation) => generation.correlationId !== correlationId),
    );
  }, []);

  const listQueryKey = React.useMemo(
    () => personaOpportunitySetKeys.list(normalizedPersonaOpportunitySetTenantId(activeTenantId)),
    [activeTenantId],
  );

  const removeOptimisticSet = React.useCallback(
    (setId: string) => {
      if (!activeTenantId) return;
      queryClient.setQueryData<PersonaOpportunitySetListPage>(
        listQueryKey,
        (prev) => removePersonaSetFromList(prev, setId),
      );
    },
    [activeTenantId, listQueryKey, queryClient],
  );

  /** Card/workspace loading UI keys off cached GENERATING — flip before the list poll. */
  const markSetGeneratingOptimistically = React.useCallback(
    (setId: string) => {
      if (!activeTenantId) return;
      queryClient.setQueryData<PersonaOpportunitySetListPage>(
        listQueryKey,
        (prev) => markPersonaSetGeneratingInList(prev, setId),
      );
      queryClient.setQueryData<PersonaOpportunitySetDetail>(
        personaOpportunitySetKeys.detail(
          normalizedPersonaOpportunitySetTenantId(activeTenantId),
          setId,
        ),
        (prev) => markPersonaSetGeneratingInDetail(prev, setId),
      );
    },
    [activeTenantId, listQueryKey, queryClient],
  );

  const bindRealSetId = React.useCallback(
    (generation: PendingGeneration, realSetId: string) => {
      if (!activeTenantId) return;
      queryClient.setQueryData<PersonaOpportunitySetListPage>(
        listQueryKey,
        (prev) => replacePersonaSetIdInList(prev, generation.placeholderId, realSetId),
      );
      // A list poll that ran before the worker inserted the row has already dropped
      // the placeholder, leaving nothing GENERATING to keep the poll alive. The row
      // exists by the time its id is published, so read it now.
      void queryClient.invalidateQueries({ queryKey: listQueryKey });
      const watchingPlaceholder =
        search.setId === generation.placeholderId ||
        search.setIds === generation.placeholderId;
      if (watchingPlaceholder || generation.autoOpen) {
        openHubForSet(realSetId, { replace: true });
      }
    },
    [activeTenantId, listQueryKey, openHubForSet, queryClient, search.setId, search.setIds],
  );

  const returnToSetsOverview = React.useCallback(() => {
    void navigateRef.current({
      search: (previous) => ({
        ...previous,
        setId: undefined,
        tab: defaultSearch.tab,
        view: defaultSearch.view,
      }),
      replace: true,
    });
  }, []);

  // Bind each run's real set id onto its own optimistic card as the task record
  // publishes it. Runs resolve independently and in any order.
  React.useEffect(() => {
    if (pendingGenerations.length === 0) return;

    const resolved = pendingGenerations.flatMap((generation) => {
      const task = activeTasks.find((t) => t.correlationId === generation.correlationId);
      const setId = task?.navigation?.params?.setId;
      return typeof setId === "string" && setId ? [{ generation, setId }] : [];
    });
    if (resolved.length === 0) return;

    for (const { generation, setId } of resolved) {
      bindRealSetId(generation, setId);
    }
    setPendingGenerations((prev) =>
      prev.filter(
        (generation) =>
          !resolved.some((entry) => entry.generation.correlationId === generation.correlationId),
      ),
    );
  }, [pendingGenerations, activeTasks, bindRealSetId]);

  // The task record is the only end-of-run signal: it disappears on cancel and on
  // completion alike. Disappearance only counts once the task has been seen at
  // least once, otherwise the gap before the first discovery poll looks like one.
  React.useEffect(() => {
    if (pendingGenerations.length === 0) return;
    const liveCorrelationIds = new Set(activeTasks.map((task) => task.correlationId));

    const ended = pendingGenerations.filter(
      (generation) => generation.taskSeen && !liveCorrelationIds.has(generation.correlationId),
    );
    const newlySeen = pendingGenerations.filter(
      (generation) => !generation.taskSeen && liveCorrelationIds.has(generation.correlationId),
    );
    if (ended.length === 0 && newlySeen.length === 0) return;

    // A placeholder still standing here will never be filled — its run ended
    // without ever publishing a set id, so the card represents nothing.
    for (const generation of ended) {
      removeOptimisticSet(generation.placeholderId);
    }

    setPendingGenerations((prev) =>
      prev
        .filter(
          (generation) =>
            !ended.some((entry) => entry.correlationId === generation.correlationId),
        )
        .map((generation) =>
          newlySeen.some((entry) => entry.correlationId === generation.correlationId)
            ? { ...generation, taskSeen: true }
            : generation,
        ),
    );
    // Leaving a workspace whose set was cancelled is the "selected set vanished
    // from the list" effect's job — a completed run also lands here, and it must
    // not eject the user from the set they were waiting for.
  }, [pendingGenerations, activeTasks, removeOptimisticSet]);

  const generateSet = useGeneratePersonaOpportunitySet();

  const deleteSet = useDeletePersonaOpportunitySet();

  const renameSet = useRenamePersonaOpportunitySet();

  const retrySet = useRetryPersonaOpportunitySet();

  const regenerateSet = useRegeneratePersonaOpportunitySet();

  // After a cancel removes the set from cache, leave the workspace immediately — do not
  // wait on detail reads; stale detail must not keep a soft-deleted set open.
  React.useEffect(() => {
    const setId = search.setId;
    if (!setId || setsLoading) return;
    if (sets.some((set) => set.id === setId)) return;
    returnToSetsOverview();
  }, [search.setId, sets, setsLoading, returnToSetsOverview]);

  // The effect clears `setIds` after paint. Until then, a tenant change must not
  // query the new tenant with the previous tenant's selection.
  const selectedSetIds = React.useMemo(() => {
    if (previousTenantIdRef.current !== activeTenantId) return EMPTY_HUB_SET_IDS;
    if (!search.setIds) return EMPTY_HUB_SET_IDS;
    return search.setIds.split(",").filter(Boolean);
  }, [activeTenantId, search.setIds]);

  const detailSetId =
    search.setId ?? (selectedSetIds.length === 1 ? selectedSetIds[0] : undefined);
  const isScriptsGenerating = isPersonaScriptsGenerationActiveForSet(activeTasks, detailSetId);
  const hubScriptsLiveRefetchMs = isPersonaScriptsGenerationActiveForAnySet(
    activeTasks,
    selectedSetIds,
  )
    ? PERSONA_SCRIPTS_LIVE_REFETCH_MS
    : false;

  const { data: detail, isLoading: isDetailLoading, isFetching: isDetailFetching } = useQuery(
    personaOpportunitySetDetailQueryOptions(activeTenantId, detailSetId, {
      enabled: canListSets,
      pollMs: isScriptsGenerating ? PERSONA_SCRIPTS_LIVE_REFETCH_MS : false,
    }),
  );

  const handleHubTabChange = React.useCallback((hubTab: OpportunityHubTabKey) => {
    void navigate({ search: (previous) => ({ ...previous, hubTab }) });
  }, [navigate]);

  const handleSelectedSetIdsChange = React.useCallback(
    (setIds: string[]) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          setIds: setIds.length > 0 ? setIds.join(",") : undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleTabChange = React.useCallback((tab: PersonaOpportunityTabKey) => {
    void navigate({
      search: (previous) => ({
        ...previous,
        tab,
      }),
    });
  }, [navigate]);

  const handleViewChange = React.useCallback((view: PersonaOpportunityViewMode) => {
    void navigate({
      search: (previous) => ({
        ...previous,
        view,
      }),
    });
  }, [navigate]);

  const handleGroupByChange = React.useCallback((groupBy: PersonaOpportunityGroupBy) => {
    void navigate({
      search: (previous) => ({
        ...previous,
        groupBy,
      }),
    });
  }, [navigate]);

  const handleSearchChange = React.useCallback(
    (q: string) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          q: q.trim() ? q : undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleStatusFilterChange = React.useCallback(
    (status: PersonaOpportunityStatusFilter) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          status,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleLegacyFacetsChange = React.useCallback(
    (next: {
      action: string;
      dataset: string;
      flowName: string;
      feature: string;
      subFeature: string;
      engine: string;
    }) => {
      void navigate({
        search: (previous) => ({
          ...previous,
          ...legacyFacetsToSearch(next),
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleSetChange = React.useCallback((set: PersonaOpportunitySet | null) => {
    if (!set) {
      // Back to the grid is a deliberate choice to stay there: no in-flight run
      // may yank the user into a workspace when it finishes.
      setPendingGenerations((prev) =>
        prev.some((generation) => generation.autoOpen)
          ? prev.map((generation) => ({ ...generation, autoOpen: false }))
          : prev,
      );
      void navigate({
        search: (previous) => ({
          ...previous,
          setId: undefined,
          tab: defaultSearch.tab,
          view: defaultSearch.view,
        }),
      });
      return;
    }
    void navigate({
      search: (previous) => ({
        ...previous,
        setId: undefined,
        setIds: set.id,
        hubTab: hubTabForWorkspaceTab(previous.tab),
        createSet: undefined,
      }),
    });
  }, [navigate]);

  const handleStopSet = React.useCallback((set: PersonaOpportunitySet) => {
    if (isPendingPersonaSetId(set.id)) {
      // No server row yet, so the correlation is the only handle on the job —
      // and it must be this card's own run, not whichever started most recently.
      const generation = pendingGenerations.find(
        (candidate) => candidate.placeholderId === set.id,
      );
      if (generation) {
        void cancelTask(generation.correlationId);
        void dismissTask(generation.correlationId).catch(() => {});
        untrackPendingGeneration(generation.correlationId);
      }
      void queryClient.invalidateQueries({ queryKey: personaOpportunityHubKeys.all });
      removeOptimisticSet(set.id);
    } else if (!stopPersonaOpportunityGeneration(set.id)) {
      // Nothing was cancelled: no cancellable task matched, so the worker is
      // still running. Clearing the card and navigating away would tell the user
      // it stopped, then hand them the same GENERATING card on the next poll.
      toast.error(
        t(
          "personaOpportunities.toast.stopFailed",
          "Couldn't stop this generation — it may have already finished.",
        ),
      );
      return;
    }

    queryClient.removeQueries({
      queryKey: personaOpportunitySetKeys.detail(
        normalizedPersonaOpportunitySetTenantId(activeTenantId),
        set.id,
      ),
    });
    if (search.setId === set.id || set.status === "GENERATING") {
      returnToSetsOverview();
    }
  }, [
    activeTenantId,
    pendingGenerations,
    queryClient,
    removeOptimisticSet,
    returnToSetsOverview,
    search.setId,
    stopPersonaOpportunityGeneration,
    t,
    untrackPendingGeneration,
  ]);

  const handleGenerateSet = React.useCallback((submit: CreatePersonaSetSubmit) => {
    if (!activeTenantId) return;

    const placeholderId = createPendingPersonaSetId();
    queryClient.setQueryData<PersonaOpportunitySetListPage>(
      listQueryKey,
      (prev) =>
        applyOptimisticCreateToList(
          prev,
          createOptimisticPersonaSetRow(submit, activeTenantId, placeholderId),
        ),
    );

    generateSet.mutate(
      buildGeneratePersonaOpportunitySetRequest(submit, {
        tenantId: activeTenantId,
        idempotencyKey: crypto.randomUUID(),
        canUseQueryTuning,
      }),
      {
        onSuccess: ({ correlationId, setId }) => {
          if (setId) {
            queryClient.setQueryData<PersonaOpportunitySetListPage>(
              listQueryKey,
              (prev) => replacePersonaSetIdInList(prev, placeholderId, setId),
            );
            trackPendingGeneration(correlationId, setId, true);
            return;
          }
          trackPendingGeneration(correlationId, placeholderId, true);
        },
        onError: () => removeOptimisticSet(placeholderId),
      },
    );
  }, [
    activeTenantId,
    canUseQueryTuning,
    generateSet,
    listQueryKey,
    queryClient,
    removeOptimisticSet,
    trackPendingGeneration,
  ]);

  const handleDeleteSet = React.useCallback(
    (set: PersonaOpportunitySet) => {
      deleteSet.mutate(set.id, {
        onSuccess: () => {
          if (search.setId === set.id) {
            returnToSetsOverview();
          }
        },
      });
    },
    [deleteSet, returnToSetsOverview, search.setId],
  );

  const handleRenameSet = React.useCallback(
    (id: string, name: string) => {
      renameSet.mutate({ id, name });
    },
    [renameSet],
  );

  const handleRetrySet = React.useCallback(
    (set: PersonaOpportunitySet) => {
      if (!activeTenantId) return;
      markSetGeneratingOptimistically(set.id);
      // The set already exists, so there is nothing to wait for before showing it.
      openHubForSet(set.id, { replace: true });
      retrySet.mutate(
        {
          setId: set.id,
          body: {
            tenantId: activeTenantId,
            idempotencyKey: crypto.randomUUID(),
          },
        },
        {
          onError: () => {
            void queryClient.invalidateQueries({ queryKey: listQueryKey });
            void queryClient.invalidateQueries({
              queryKey: personaOpportunitySetKeys.detail(
                normalizedPersonaOpportunitySetTenantId(activeTenantId),
                set.id,
              ),
            });
          },
        },
      );
    },
    [
      activeTenantId,
      listQueryKey,
      markSetGeneratingOptimistically,
      openHubForSet,
      queryClient,
      retrySet,
    ],
  );

  /** The failure toast only carries a set id, not the row it came from. */
  const handleRetrySetById = React.useCallback(
    (setId: string) => {
      const set = sets.find((candidate) => candidate.id === setId);
      if (set) handleRetrySet(set);
    },
    [handleRetrySet, sets],
  );

  const handleRegenerateSet = React.useCallback(
    (input: {
      set: PersonaOpportunitySet;
      mode: "overwrite" | "new_set";
      name?: string;
    }) => {
      if (!activeTenantId) return;
      // Overwrite reuses the set id, so only new_set needs a placeholder.
      let placeholderId: string | null = null;

      if (input.mode === "overwrite") {
        markSetGeneratingOptimistically(input.set.id);
      } else {
        const newSetPlaceholderId = createPendingPersonaSetId();
        placeholderId = newSetPlaceholderId;
        queryClient.setQueryData<PersonaOpportunitySetListPage>(
          listQueryKey,
          (prev) =>
            applyOptimisticCreateToList(
              prev,
              createOptimisticGeneratingSetRow({
                id: newSetPlaceholderId,
                tenantId: activeTenantId,
                name: input.name?.trim() || input.set.name,
                industry: input.set.industry,
                industryCompetitors: input.set.industryCompetitors,
                adPlatform: input.set.adPlatform,
              }),
            ),
        );
        // Leave the source workspace so the new GENERATING card is visible on the grid.
        returnToSetsOverview();
      }

      regenerateSet.mutate(
        {
          setId: input.set.id,
          body: {
            tenantId: activeTenantId,
            idempotencyKey: crypto.randomUUID(),
            mode: input.mode,
            ...(input.mode === "new_set" && input.name ? { name: input.name } : {}),
          },
        },
        {
          onSuccess: ({ correlationId }) => {
            if (placeholderId) trackPendingGeneration(correlationId, placeholderId, true);
          },
          onError: () => {
            if (input.mode === "overwrite") {
              void queryClient.invalidateQueries({ queryKey: listQueryKey });
              void queryClient.invalidateQueries({
                queryKey: personaOpportunitySetKeys.detail(
                  normalizedPersonaOpportunitySetTenantId(activeTenantId),
                  input.set.id,
                ),
              });
            } else if (placeholderId) {
              removeOptimisticSet(placeholderId);
            }
          },
        },
      );
    },
    [
      activeTenantId,
      listQueryKey,
      markSetGeneratingOptimistically,
      queryClient,
      regenerateSet,
      removeOptimisticSet,
      returnToSetsOverview,
      trackPendingGeneration,
    ],
  );

  usePersonaSetGenerationToasts({
    sets,
    activeTasks,
    tenantId: activeTenantId,
    onOpenSet: openHubForSet,
    onRetrySet: canCreateSet ? handleRetrySetById : undefined,
  });

  if (isLoading || (canListSets && setsLoading)) {
    return <PersonaOpportunitiesLoadingState />;
  }

  // A failed list read must not fall through to the empty state — that would invite
  // the user to create a set they may already have.
  if (error || setsError) {
    return <PageError is403={error instanceof PageAccessError} />;
  }

  if (!activeTenantId) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }

  return (
    <PersonaOpportunitiesPage
      tenantId={activeTenantId}
      sets={sets}
      selectedSet={selectedSet}
      detail={detail?.set.id === detailSetId ? detail : undefined}
      isDetailLoading={Boolean(detailSetId) && (isDetailLoading || isDetailFetching) && !detail}
      hubScriptsLiveRefetchMs={hubScriptsLiveRefetchMs}
      canListSets={canListSets}
      canCreateSet={canCreateSet}
      canCreateEmptyCustomSet={canCreateEmptyCustomSet}
      canUseQueryTuning={canUseQueryTuning}
      // Only the in-flight POST, not "a set is generating" — concurrent
      // generations are allowed, so a running set must not block a new one.
      isCreatingSet={generateSet.isPending}
      isDeletingSet={deleteSet.isPending}
      isRenamingSet={renameSet.isPending}
      isRegeneratingSet={regenerateSet.isPending}
      openCreateSetOnMount={search.createSet === true && !search.setId}
      onCreateSetDialogOpened={() => {
        if (!search.createSet) return;
        void navigate({
          search: (previous) => ({ ...previous, createSet: undefined }),
          replace: true,
        });
      }}
      onSetChange={handleSetChange}
      onGenerateSet={canCreateSet ? handleGenerateSet : undefined}
      onStopSet={canCreateSet ? handleStopSet : undefined}
      onDeleteSet={canCreateSet ? handleDeleteSet : undefined}
      onRenameSet={canCreateSet ? handleRenameSet : undefined}
      onRetrySet={canCreateSet ? handleRetrySet : undefined}
      onRegenerateSet={canCreateSet ? handleRegenerateSet : undefined}
      hubTab={search.hubTab}
      onHubTabChange={handleHubTabChange}
      selectedSetIds={selectedSetIds}
      onSelectedSetIdsChange={handleSelectedSetIdsChange}
      onOpenScopedSet={openScopedSet}
      activeTab={search.tab}
      onTabChange={handleTabChange}
      view={search.view}
      onViewChange={handleViewChange}
      groupBy={search.groupBy}
      onGroupByChange={handleGroupByChange}
      search={search.q ?? ""}
      onSearchChange={handleSearchChange}
      statusFilter={search.status ?? "all"}
      onStatusFilterChange={handleStatusFilterChange}
      legacyFacets={legacyFacetsFromSearch(search)}
      onLegacyFacetsChange={handleLegacyFacetsChange}
      highlightOrderId={search.orderId}
    />
  );
}

export const Route = createFileRoute("/_authenticated/persona-opportunities")({
  beforeLoad: () => assertPagePermission("persona-opportunities"),
  validateSearch: personaOpportunitiesSearchSchema,
  loaderDeps: ({ search }) => ({ setId: search.setId }),
  loader: ({ context, deps }) => {
    const tenantId = useAuth.getState().activeTenantId;
    if (tenantId) {
      // Fire-and-forget: the component owns loading and error UI, and a viewer
      // without the list grant would only collect a 403 here.
      void context.queryClient.prefetchQuery(adPlatformsQueryOptions(tenantId));
      void context.queryClient.prefetchQuery(personaOpportunitySetsQueryOptions(tenantId));
      if (deps.setId) {
        void context.queryClient.prefetchQuery(
          personaOpportunitySetDetailQueryOptions(tenantId, deps.setId),
        );
      }
    }
    return context.queryClient
      .ensureQueryData(pageStateQueryOptions("persona-opportunities"))
      .catch((err: unknown) => {
        if (err instanceof PageAccessError) return null;
        throw err;
      });
  },
  component: PersonaOpportunitiesRoute,
  errorComponent: PageAccessErrorComponent,
});
